// ─────────────────────────────────────────────────────────────
// KeywordGod — 카테고리 카탈로그 (Phase 28)
//
// Phase 22+ 에서 추가한 시드 배치들을 사용자 브라우징 단위로 묶음.
// 각 카테고리는 매칭용 seed 단어 리스트를 가지며 — 키워드에 그 단어가 포함되면
// 해당 카테고리에 속한다고 본다 (substring 매칭).
//
// 한 키워드가 여러 카테고리에 동시 속할 수 있음 (배타적 라벨 아님).
// ─────────────────────────────────────────────────────────────

export interface KeywordCategory {
  id: string;
  label: string;
  emoji: string;
  description: string;
  /** 키워드 매칭용 단어 (substring contains) — DB LIKE '%seed%' */
  seeds: ReadonlyArray<string>;
}

export const KEYWORD_CATEGORIES: ReadonlyArray<KeywordCategory> = [
  // ─── Phase 50: 시사/정책 카테고리를 맨 앞으로 (쇼핑 편중 시각적 완화) ───
  {
    id: 'policy',
    label: '정책/지원금',
    emoji: '📋',
    description: '정부 지원금·청년정책·정부보조금·바우처',
    seeds: [
      '지원금', '보조금', '바우처', '청년월세', '청년도약', '청년희망',
      '청년내일채움', '청년취업', '신생아특례', '디딤돌', '보금자리',
      '근로장려금', '자녀장려금', '첫만남이용권', '부모급여', '양육수당',
      '아동수당', '임신출산진료비', '햇살론', '미소금융', '내일배움카드',
      '문화누리카드', '에너지바우처', '재난지원금', '민생회복', '민생지원',
      '고유가피해', '소상공인', '복지로', '정부24', '보조금24',
      '워크넷', '실업급여', '구직급여', '청년창업', '농촌청년',
    ],
  },
  {
    id: 'news',
    label: '시사/경제',
    emoji: '📰',
    description: '금리·환율·물가·국제 정세·정치·뉴스',
    seeds: [
      '기준금리', '한국은행', '환율', '달러', '엔화', '위안',
      '코스피', '코스닥', '비트코인', '이더리움', '코인 시세',
      '물가', '소비자물가', 'GDP', '경제 성장률', '경기 침체',
      '유가', '휘발유 가격', '경유 가격', 'LPG', '전기요금',
      '가스요금', '도시가스', '수도요금', '건강보험료', '최저임금',
      '주4일제', '정년연장', '국정감사', '추경', '예산',
      '대통령', '국회', '미국 대선', '트럼프', '중국 경제',
      '북한', '러시아', '우크라이나', '이스라엘', '관세',
      '반도체', '삼성전자', 'SK하이닉스', '인공지능', 'AI',
    ],
  },
  {
    id: 'tax',
    label: '세금/환급',
    emoji: '💸',
    description: '연말정산·종합소득세·환급금·세액공제',
    seeds: [
      '연말정산', '종합소득세', '소득세 신고', '부가가치세', '양도소득세',
      '종합부동산세', '종부세', '재산세', '자동차세', '주민세', '취득세',
      '등록세', '지방세', '세금환급', '미환급금', '환급금 조회',
      '홈택스', '손택스', '위택스', '자녀세액공제', '월세 세액공제',
      '의료비 세액공제', '신용카드 소득공제', '연금저축 세액공제',
      'IRP 세액공제', 'ISA 비과세', '상속세', '증여세', '금융투자소득세',
      '주식 양도', '가상자산 과세', '간이과세', '면세사업자', '사업소득',
    ],
  },
  {
    id: 'welfare',
    label: '공공복지',
    emoji: '🤝',
    description: '기초연금·노령연금·의료급여·기초수급·노인복지',
    seeds: [
      '기초연금', '노령연금', '국민연금', '기초생활수급', '차상위',
      '한부모가정', '장애인연금', '장애수당', '보훈수당', '의료급여',
      '노인장기요양', '노인맞춤돌봄', '치매검진', '치매가족', '치매안심',
      '본인부담상한', '재난적의료비', '산정특례', '희귀질환',
      '산모신생아', '산후조리', '임산부 친환경', '영양플러스',
      '국가건강검진', '비대면 진료', '돌봄서비스', '아이돌봄',
      '가족돌봄휴가', '경력단절', '맞벌이', '여성가족부',
    ],
  },

  // ─── Phase 53: 콘텐츠 장르 (Blokey 벤치마킹 — 블로거 친숙) ───
  {
    id: 'drama',
    label: '드라마',
    emoji: '📺',
    description: '한국 드라마·미드·일드·OTT 시리즈',
    seeds: [
      '드라마', '한국드라마', '미드', '일드', '중드',
      '넷플릭스 드라마', '디즈니플러스 드라마', '티빙 드라마', '웨이브 드라마', '쿠팡플레이 드라마',
      '드라마 추천', '드라마 결말', '드라마 회차', '드라마 줄거리', '드라마 시청률',
      '드라마 다시보기', '드라마 OST', '드라마 OST 가사', '드라마 촬영지', '드라마 캐스팅',
      '주말드라마', '월화드라마', '수목드라마', '금토드라마', '아침드라마',
      '사극', '로코', '로맨스 드라마', '의학 드라마', '법정 드라마',
      '오징어게임', '더글로리', '솔로지옥', '환승연애', '나는 솔로',
      '재벌집 막내아들', '이상한 변호사 우영우', '눈물의 여왕', '도깨비', '응답하라',
      '슬기로운 의사생활', '미스터선샤인', '도깨비', '시그널', '비밀의숲',
      '킹덤', '지옥', '소년심판', '수리남', '마이네임',
      '더 킹', '청춘기록', '스타트업', '빈센조', '갯마을 차차차',
      '오피스 시리즈', '굿플레이스', '브레이킹배드', '왕좌의게임', '하우스오브카드',
      '기묘한이야기', '위쳐', '오자크', '브리저튼', '베이비레인디어',
      '슬램덩크 다시보기', '명탐정 코난', '원피스', '귀멸의 칼날', '주술회전',
    ],
  },
  {
    id: 'movie',
    label: '영화',
    emoji: '🎬',
    description: '한국·헐리우드 영화·박스오피스·OTT',
    seeds: [
      '영화', '영화 추천', '영화 다시보기', '영화 결말', '영화 줄거리',
      '영화 평점', '영화 리뷰', '영화 OST', '영화관 상영시간', '영화관 예매',
      'CGV', '메가박스', '롯데시네마', '씨네큐', '인디스페이스',
      '한국영화', '독립영화', '예술영화', '저예산 영화', '단편영화',
      '헐리우드 영화', '마블 영화', 'DC 영화', '디즈니 영화', '픽사 영화',
      '봉준호', '박찬욱', '이창동', '홍상수', '김지운',
      '이병헌', '송강호', '최민식', '하정우', '마동석',
      '전지현', '손예진', '김혜수', '박보영', '아이유 영화',
      '박스오피스', '주말 박스오피스', '북미 박스오피스', '관객수', '천만 영화',
      '오펜하이머', '듄', '바비', '데드풀', '인사이드아웃 2',
      '범죄도시', '서울의 봄', '파묘', '12.12 그날', '한산',
      '베테랑', '범죄와의 전쟁', '암살', '신과함께', '엑시트',
      '기생충', '미나리', '버닝', '브로커', '헤어질 결심',
      '인터스텔라', '인셉션', '다크나이트', '아바타', '타이타닉',
      '죠스', '대부', '쇼생크 탈출', '포레스트 검프', '매트릭스',
      '존윅', '미션임파서블', '제임스본드', '007', '제이슨본',
      '아이언맨', '스파이더맨', '캡틴아메리카', '토르', '닥터스트레인지',
      '어벤져스', '엑스맨', '울버린', '데드풀과 울버린', '데드풀',
      '슈퍼맨', '배트맨', '원더우먼', '아쿠아맨', '플래시',
    ],
  },
  {
    id: 'celeb',
    label: '스타·연예인',
    emoji: '⭐',
    description: 'K팝 아이돌·배우·예능인·인플루언서',
    seeds: [
      '연예인', '아이돌', '걸그룹', '보이그룹', 'K팝',
      '데뷔', '컴백', '솔로 데뷔', '미니앨범', '정규앨범',
      '뉴진스', '아이브', '르세라핌', '에스파', '있지',
      '블랙핑크', '트와이스', '소녀시대', '레드벨벳', '마마무',
      'BTS', '세븐틴', '엔하이픈', '투모로우바이투게더', '엑소',
      '스트레이키즈', '에이티즈', '아이콘', '몬스타엑스', '갓세븐',
      '제니', '지수', '로제', '리사', '아이유',
      '태연', '윤아', '수지', '소희', '제시카',
      '뷔', '정국', '지민', '제이홉', '슈가',
      '카리나', '윈터', '닝닝', '지젤', '하니',
      '민지', '다니엘', '해린', '혜인', '안유진',
      '장원영', '레이', '리즈', '가을', '이서',
      '아이브 컴백', '뉴진스 컴백', '르세라핌 컴백', '에스파 컴백',
      '데이식스', '엔플라잉', '검정치마', '잔나비', '10cm',
      '아이유 콘서트', 'BTS 콘서트', '블랙핑크 콘서트', '뉴진스 콘서트',
      '백상예술대상', '청룡영화상', '대종상', '한국방송대상', '연예대상',
      '연예 가십', '연예인 결혼', '연예인 이혼', '연예인 열애', '연예인 임신',
      '한지민', '김희애', '이영애', '전도연', '문소리',
      '이병헌 결혼', '김하늘 출산', '한가인', '정유미', '조진웅',
      '유재석', '강호동', '이수근', '신동엽', '김구라',
      '런닝맨', '무한도전', '나는 솔로', '환승연애', '돌싱글즈',
      '런닝맨 게스트', '런닝맨 다시보기', '런닝맨 멤버', '런닝맨 댄스',
      '아이브 안유진', '아이브 장원영', '뉴진스 민지', '뉴진스 하니', '에스파 카리나',
      '한국 인플루언서', 'V로그', '먹방', '하석진', '곽튜브',
    ],
  },
  {
    id: 'sports',
    label: '스포츠',
    emoji: '⚽',
    description: '축구·야구·골프·올림픽·KBO·EPL·MLB',
    seeds: [
      '축구', '야구', '농구', '배구', '골프',
      '테니스', '복싱', 'MMA', 'UFC', 'WWE',
      '한국 축구', '국가대표 축구', '월드컵', '아시안컵', '올림픽',
      '손흥민', '이강인', '김민재', '황희찬', '황의조',
      '박지성', '차범근', '안정환', '박주영', '기성용',
      '토트넘', '맨유', '리버풀', '맨시티', '아스널',
      '첼시', '레알마드리드', '바르셀로나', '바이에른 뮌헨', '파리 생제르맹',
      '프리미어리그', 'EPL', '라리가', '분데스리가', '세리에A',
      'UEFA 챔피언스리그', 'UEFA 유로파', 'FA컵', '코파아메리카', 'AFC 챔피언스리그',
      '리오넬 메시', '크리스티아누 호날두', '음바페', '엘링 홀란드', '카림 벤제마',
      'KBO', '두산베어스', 'LG트윈스', '롯데자이언츠', 'SSG랜더스',
      '키움히어로즈', '한화이글스', 'NC다이노스', 'KIA타이거즈', '삼성라이온즈',
      'KT위즈', '한국시리즈', '플레이오프', '준플레이오프', '와일드카드',
      '메이저리그', 'MLB', '류현진', '김하성', '이정후',
      '오타니 쇼헤이', '클레이튼 커쇼', '아론 저지', '브라이스 하퍼', '마이크 트라웃',
      'LA 다저스', '뉴욕 양키스', '샌디에이고 파드리스', '필라델피아 필리스', '시카고 컵스',
      '한국야구', '야구 일정', '야구 중계', '야구 순위', '야구 기록',
      'NBA', 'KBL', '농구 결승', '르브론 제임스', '스테판 커리',
      '농구 국가대표', '아시안게임 농구', '하승진', '서장훈', '허재',
      'V리그', '여자배구', '남자배구', '김연경', '이재영',
      '도쿄 올림픽', '파리 올림픽', '베이징 올림픽', '동계 올림픽', '올림픽 메달',
      '안세영', '신유빈', '오상욱', '구본길', '김우민',
      '박세리', '김효주', '고진영', '박성현', '리디아 고',
      '타이거 우즈', '로리 매킬로이', '브룩스 켑카', '존 람', '스카티 셰플러',
      '마스터스 골프', 'PGA 챔피언십', 'US 오픈 골프', '디오픈', 'LPGA',
      '한국 골프 선수', '여자골프 우승', '남자골프 우승', 'PGA 투어 순위',
      'F1 그랑프리', 'F1 드라이버', '맥스 페르스타펜', '루이스 해밀턴',
      'NFL 슈퍼볼', 'MLS 축구', 'WBC 한국', 'WBC 일본',
      '이대호', '강민호', '양현종', '김광현', '추신수',
      '한국 e스포츠', 'LCK', '롤드컵', 'T1 페이커', 'Faker',
    ],
  },

  {
    id: 'realestate',
    label: '부동산',
    emoji: '🏠',
    description: '아파트·분양·전세·매매 등 주거/투자 부동산',
    seeds: [
      '아파트', '분양', '전세', '매매', '청약', '재개발', '재건축',
      '오피스텔', '빌라', '원룸', '투룸', '주택', '주거', '임대', '월세',
      '보증금', '갭투자', '경매', '상가', '토지', '부동산',
    ],
  },
  {
    id: 'wedding',
    label: '결혼/웨딩',
    emoji: '💒',
    description: '신혼 준비 · 웨딩촬영 · 결혼식장 · 청첩장',
    seeds: ['웨딩', '신혼', '결혼', '청첩장', '스드메', '예단', '예물', '혼수', '신혼여행', '본식'],
  },
  {
    id: 'insurance',
    label: '보험',
    emoji: '🛡️',
    description: '자동차·실비·생명·암 등 보험 상품 비교',
    seeds: ['보험', '실비', '암보험', '운전자보험', '종신', '연금보험'],
  },
  {
    id: 'finance',
    label: '금융/투자',
    emoji: '💰',
    description: '주식·펀드·적금·대출·카드·코인',
    seeds: [
      '주식', '펀드', '적금', '예금', '대출', '신용카드', 'ETF', '코인',
      '비트코인', '청년도약', 'ISA', '투자', '재테크', '연금', '저축',
      '환율', '금리', '체크카드', '카드',
    ],
  },
  {
    id: 'medical',
    label: '의료/병원',
    emoji: '🏥',
    description: '피부과·치과·정형외과 등 진료/시술',
    seeds: [
      '피부과', '정형외과', '치과', '안과', '산부인과', '한의원', '임플란트',
      '라식', '시술', '한약', '병원', '진료', '수술', '검진', '약국',
      '한방', '재활', '내과', '외과', '소아과',
    ],
  },
  {
    id: 'pet',
    label: '반려동물',
    emoji: '🐾',
    description: '강아지·고양이 사료·용품·동물병원',
    seeds: ['강아지', '고양이', '반려동물', '동물병원', '펫', '햄스터', '토끼', '앵무새', '캣타워', '어항'],
  },
  {
    id: 'career',
    label: '직장/취업',
    emoji: '💼',
    description: '이직·연봉·자소서·면접·자격증',
    seeds: [
      '이직', '연봉', '자기소개서', '자소서', '면접', '취업', '채용',
      '공기업', '공무원', '자격증', '직장', '회사', '월급', '직무',
      '커리어', '신입', '경력직', '인사', '이력서', '구직',
    ],
  },
  {
    id: 'legal',
    label: '법률/세무',
    emoji: '⚖️',
    description: '변호사·세무사·소송·세금',
    seeds: ['변호사', '세무사', '법무사', '이혼', '상속', '유언장', '소송', '소득세', '부가가치세', '증여세', '사업자등록'],
  },
  {
    id: 'food',
    label: '요리/식품',
    emoji: '🍳',
    description: '레시피·밀키트·새벽배송·집밥',
    seeds: ['김치찌개', '된장찌개', '갈비찜', '비빔밥', '도시락', '밀키트', '새벽배송', '떡볶이', '치킨', '백종원', '집밥', '레시피', '한식', '양식'],
  },
  {
    id: 'academy',
    label: '학원/시험',
    emoji: '📚',
    description: '공인중개사·토익·공무원시험·인강',
    seeds: ['공인중개사', '토익', '토플', '한국사', '컴퓨터활용', '공무원시험', '자격증인강', '학원', '인강', '과외'],
  },
  {
    id: 'travel',
    label: '여행',
    emoji: '✈️',
    description: '일본·동남아·유럽 여행 + 호텔/항공권',
    seeds: ['여행', '호텔', '항공권', '비행기표', '패키지', '호캉스', '펜션', '오사카', '도쿄', '후쿠오카', '다낭', '베트남', '태국'],
  },
  {
    id: 'game',
    label: '게임/취미',
    emoji: '🎮',
    description: '온라인 게임·콘솔·보드·만화',
    seeds: ['롤', '발로란트', '오버워치', '배그', '메이플', '카트라이더', '닌텐도', 'PS5', '스팀게임', '모바일게임', '보드게임', '레고', '프라모델', '피규어', '만화', '웹툰'],
  },
  {
    id: 'beauty',
    label: '뷰티/미용',
    emoji: '💄',
    description: '메이크업·헤어·시술',
    seeds: ['마스카라', '아이라이너', '아이섀도우', '파운데이션', '컨실러', '블러셔', '매니큐어', '네일', '염색', '펌', '미용실', '두피', '탈모', '보톡스', '필러', '립스틱', '쿠션', '향수', '스킨', '선크림'],
  },
  {
    id: 'fitness',
    label: '헬스/피트니스',
    emoji: '🏋️',
    description: '운동·다이어트·단백질·요가/필라테스',
    seeds: [
      '헬스장', '단백질', '쉐이크', '요가', '필라테스', '크로스핏', '닭가슴살',
      '그릭요거트', '곤약', '프로틴', '다이어트', '비만', 'PT', '운동', '헬스',
      '체중', '체지방', '근육', '러닝', '걷기',
    ],
  },
  {
    id: 'kids-edu',
    label: '자녀교육',
    emoji: '🎒',
    description: '영유아 학습·학원·수능/입시',
    seeds: ['어린이영어', '초등영어', '학습지', '학원비', '사교육', '영재', '수능', '수시', '정시', '입시', '의대', '약대', '한의대', '영어유치원', '중학교', '고등학교'],
  },
  {
    id: 'interior',
    label: '인테리어',
    emoji: '🛋️',
    description: '거실·침실·주방 인테리어 + 가구/소품',
    seeds: ['인테리어', '리모델링', '벽지', '장판', '커튼', '블라인드', '카페트', '러그', '화분', '다육이', '몬스테라', '식물', '소파', '쇼파', '침대', '책상', '의자', '조명', '베란다'],
  },
  {
    id: 'auto',
    label: '자동차',
    emoji: '🚗',
    description: '신차·중고차·렌트·정비',
    seeds: ['아반떼', '쏘나타', '그랜저', '카니발', '팰리세이드', '제네시스', 'BMW', '벤츠', '아우디', '테슬라', '중고차', '자동차렌트', '자동차리스', '타이어', '엔진오일', '블랙박스', 'SUV', '전기차'],
  },
  {
    id: 'shopping',
    label: '온라인쇼핑',
    emoji: '🛍️',
    description: '쿠팡·무신사·마켓컬리 등 이커머스',
    seeds: ['쿠팡', '무신사', '지그재그', '에이블리', '마켓컬리', '오아시스', '쓱배송', '11번가', 'G마켓', '옥션', '위메프', '티몬', '다이소', '미니소', '알리'],
  },
  {
    id: 'tech',
    label: '전자/IT',
    emoji: '💻',
    description: '맥북·아이폰·노트북·주변기기',
    seeds: ['맥북', '아이폰', '갤럭시', '에어팟', '노트북', '키보드', '마우스', '모니터', '게이밍', 'VR'],
  },
];

/** id 로 카테고리 찾기 */
export function getCategoryById(id: string): KeywordCategory | undefined {
  return KEYWORD_CATEGORIES.find((c) => c.id === id);
}

/**
 * 키워드와 매칭되는 모든 카테고리 (substring contains).
 * 한 키워드가 여러 카테고리에 속할 수 있음.
 */
export function matchCategoriesForKeyword(keyword: string): KeywordCategory[] {
  const lower = keyword.toLowerCase();
  return KEYWORD_CATEGORIES.filter((c) =>
    c.seeds.some((s) => lower.includes(s.toLowerCase())),
  );
}

/**
 * 키워드와 가장 잘 맞는 단일 카테고리 (가장 긴 매칭 seed 우선).
 * 매칭이 없으면 null.
 */
export function primaryCategoryForKeyword(keyword: string): KeywordCategory | null {
  const lower = keyword.toLowerCase();
  let bestCat: KeywordCategory | null = null;
  let bestSeedLen = 0;
  for (const c of KEYWORD_CATEGORIES) {
    for (const s of c.seeds) {
      if (lower.includes(s.toLowerCase()) && s.length > bestSeedLen) {
        bestSeedLen = s.length;
        bestCat = c;
      }
    }
  }
  return bestCat;
}

// ─── Phase 55: 키워드 성격 분류 (쇼핑 억제 / 정보성 우선) ─────

/** 블로거가 글로 쓰는 정보성 패턴 — 항상 수집 우선 */
const INFO_PATTERNS = [
  '방법', '하는법', '하는 법', '하는방법', '추천', '후기', '원인', '증상',
  '예방', '효과', '차이', '비교', '순위', '팁', '노하우', '정리', '총정리',
  '뜻', '의미', '종류', '관리', '해결', '고치는', '없애는', '제거', '청소',
  '레시피', '만들기', '사용법', '설정', '신청방법', '계산', '기준', '조건',
  '자격', '준비물', '시기', '주의사항', '부작용', '운동', '스트레칭', '식단',
];

/** 순수 상거래/제품 구매 신호 — 1/10 로 억제 */
const SHOPPING_PATTERNS = [
  '쿠팡', '직구', '최저가', '특가', '핫딜', '정품', '가품', '중고',
  '렌탈', '렌트', '대여', '매입', '판매처', '구입', '구매처', '파는곳',
  '도매', '소매', '오픈마켓', '무신사', '에이블리', '지그재그', '11번가',
  '브랜드추천', '브랜드순위', '제품추천',
];

export type KeywordNature = 'info' | 'shopping' | 'neutral';

/**
 * 키워드 성격 분류.
 *   - info: 정보성 (블로거 글감) — 항상 수집
 *   - shopping: 순수 상거래 — 수집 억제 대상
 *   - neutral: 그 외 — 정상 수집
 * 정보성 신호가 있으면 쇼핑 신호가 있어도 info 우선 (예: "에어컨 추천 방법").
 */
export function classifyKeywordNature(keyword: string): KeywordNature {
  const lower = keyword.toLowerCase();
  for (const p of INFO_PATTERNS) {
    if (lower.includes(p)) return 'info';
  }
  for (const p of SHOPPING_PATTERNS) {
    if (lower.includes(p)) return 'shopping';
  }
  return 'neutral';
}

/**
 * 쇼핑 키워드를 확률적으로 drop 할지 결정 (수집을 1/keepRatio 로 감축).
 * Phase 55: 쇼핑 키워드가 풀에 과다 → 신규 유입 1/10 로 억제.
 */
export function shouldDropShoppingSeed(keyword: string, keepRatio = 0.1): boolean {
  if (classifyKeywordNature(keyword) !== 'shopping') return false;
  return Math.random() >= keepRatio;
}
